import type { ProductAccessInput, UserProductRole } from '@adminhub/graphql-types';

import type { ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

export const unifiedUserProfileScreen = 'unifiedUserProfileScreen';
export const unifiedUserListScreen = 'unifiedUserListScreen';
export const unifiedGroupProfileScreen = 'unifiedGroupProfileScreen';
export const serviceAccountDetailsScreen = 'serviceAccountDetailsScreen';
export const userProfileSidepanel = 'userProfileSidepanel';
export const createGroupGrantAccessFocusTask = 'orgCreateGroupGrantAccessFocusTask';

export type UULSource = typeof unifiedUserListScreen;
export type UUPSource = typeof unifiedUserProfileScreen;
export type UserProfileSidepanelSource = typeof userProfileSidepanel;
export type ServiceAccountSource = typeof serviceAccountDetailsScreen;

export interface VortexSiteAdminAttributes {
  vortexStatus: boolean;
  tenantCount?: number;
  enhancedSiteAdmin: boolean;
  userRole?: string;
}

export const unifiedUserManagementEvents = {
  source: {
    createGroupGrantAccessFocusTask,
  },
  confluenceSpecUUL: 'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/4322133136/UUL+Analytic+Specs+M1',
  confluenceSpecUUP: 'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/5144024035/Analytics+Spec+UP+M1.5',

  screen: {
    createGroupGrantAccessFocusTask: (attributes?: { currentStep?: number }): ScreenEvent => ({
      data: {
        name: createGroupGrantAccessFocusTask,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93000',
    }),
  },

  ui: {
    actionColumnButtonClicked: (source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'actionColumn',
        source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74758',
    }),
    addToGroupButtonClicked: (attributes: { userId: string; userStatus: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addToGroups',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14644',
    }),
    addUserToGroupsModalConfirmClicked: (
      source: UULSource | UUPSource | ServiceAccountSource | UserProfileSidepanelSource,
      attributes: {
        groupIds: string[];
        userId: string;
        userStatus?: string;
        workspaceIds?: string[];
        productIds?: string[];
      },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addGroupsToUserConfirm',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/40221',
    }),
    assignOrganizationRole: (
      attributes: {
        userId: string;
        userStatus?: string;
      },
      source: string,
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'assignOrganizationRole',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33731',
    }),
    assignOrganizationRoleConfirm: (attributes: { userId: string; orgId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'assignOrganizationRoleConfirm',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/40606',
    }),
    cancelDeletionButtonClicked: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDelete',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14599',
    }),
    menuDropdownOpenSiteAdminModal: (attributes: VortexSiteAdminAttributes, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'siteAdminMenu',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/66692',
    }),
    openAddProductsModal: (attributes: { userId: string; orgId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'openAddProductsModal',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/40604',
    }),
    promptPasswordResetButtonClicked: (attributes: { userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'promptPasswordReset',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33732',
    }),
    resendInviteButtonClicked: (attributes: { userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resendInvite',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14640',
    }),
    resendInviteManagedAccountButtonClicked: (attributes: { userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resendInvite',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14640',
    }),
    reactivateAcccountModalButton: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'reactivateMemberModal',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15112',
    }),
    removeUserButtonClicked: (attributes: { userId: string; userStatus: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeUser',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33960',
    }),
    suspendAccessButtonClicked: (
      attributes: { lastSeenOnSite?: string; userId: string; userStatus: string },
      source: UULSource | UUPSource | ServiceAccountSource,
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'suspendAccess',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33596',
    }),
    confirmDeactivateAccountClicked: (attributes: { orgId: string; memberId: string; products: string[] }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmMemberDeactivation',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15144',
    }),
    cancelDeactivateAccountClicked: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'closeMemberDeactivation',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15145',
    }),
    confirmDeleteAccountClicked: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteAccount',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14570',
    }),
    cancelDeleteAccountClicked: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDelete',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14599',
    }),
    restoreAccessButtonClicked: (
      attributes: { lastSeenOnSite?: string; userId: string; userStatus: string },
      source: UULSource | UUPSource | ServiceAccountSource,
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resumeAccess',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33597',
    }),
    resetPasswordButtonClicked: (attributes: { orgId: string; userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'resetPassword',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14430',
    }),
    reactivateAccountButtonClicked: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'reactivateMemberModal',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15112',
    }),
    deactivateAccountButtonClicked: (attributes: { orgId: string; userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deactivateAccount',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15124',
    }),
    closeReactivateDialogButton: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'closeReactivate',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15113',
    }),
    confirmReactivateDialogButton: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmReactivate',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15114',
    }),
    deleteAccountButtonClicked: (attributes: { orgId: string; userId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteAccount',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14570',
    }),
    closeResetPasswordDialogButton: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'closeResetPassword',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92989?tabIndex=Destinations',
    }),
    confirmResetPasswordDialogButton: (attributes: { orgId: string; memberId: string }, source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmResetPassword',
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92987',
    }),
    closeCreateGroupClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'closeCreateGroup',
        source: createGroupGrantAccessFocusTask,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93073',
    }),
    createGroupNextClicked: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createGroupNext',
        source: createGroupGrantAccessFocusTask,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93072',
    }),
    grantAccessClicked: (attributes: { roleCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'grantAccess',
        source: createGroupGrantAccessFocusTask,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/35413',
    }),
  },
  track: {
    grantedOrgAdminAccess: (attributes: { userId: string; orgId: string }, source: string): TrackEvent => ({
      data: {
        action: TrackAction.Granted,
        actionSubject: TrackActionSubject.OrgAdminAccess,
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34886',
    }),
    revokedOrgAdminAccess: (attributes: { userId: string; orgId: string }, source: string): TrackEvent => ({
      data: {
        action: TrackAction.Revoked,
        actionSubject: TrackActionSubject.OrgAdminAccess,
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34887',
    }),
    impersonateUser: (attributes: { userId: string; orgId: string }, source: string): TrackEvent => ({
      data: {
        action: TrackAction.Impersonated,
        actionSubject: TrackActionSubject.User,
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/14242',
    }),
    grantedProductAccess: (
      source: string | undefined,
      attributes: { products: ProductAccessInput[]; userId: string; userStatus?: string; userRole?: string; analyticsEventId?: string },
    ): TrackEvent => ({
      data: {
        action: TrackAction.Granted,
        actionSubject: TrackActionSubject.ProductAccess,
        source: source || unifiedUserProfileScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34927',
    }),
    revokedProductAccess: (
      source: string | undefined,
      attributes: { products: ProductAccessInput[]; userId: string; userStatus?: string; userRole?: string; analyticsEventId?: string },
    ): TrackEvent => ({
      data: {
        action: TrackAction.Revoked,
        actionSubject: TrackActionSubject.ProductAccess,
        source: source || unifiedUserProfileScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/34928',
    }),
    addedProductToGroup: (
      source: string | undefined,
      attributes: {
        groupId: string;
        grantedRole: UserProductRole;
        productId: string;
        workspaceId: string;
      },
    ): TrackEvent => ({
      data: {
        action: TrackAction.AddProduct,
        actionSubject: TrackActionSubject.Group,
        source: source || unifiedGroupProfileScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33970',
    }),
    removeProductFromGroup: (
      source: string | undefined,
      attributes: { groupId: string; revokedRole: UserProductRole; productId: string; workspaceId: string },
    ): TrackEvent => ({
      data: {
        action: TrackAction.RemoveProduct,
        actionSubject: TrackActionSubject.Group,
        source: source || unifiedGroupProfileScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/33868',
    }),
    memberDeactivated: (source: string, attributes: { memberId: string; isBulkAction: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Deactivated,
        actionSubject: TrackActionSubject.Member,
        source,
        attributes: {
          memberId: attributes.memberId,
          bulkAction: attributes.isBulkAction,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/22144',
    }),
    memberDeleted: (source: string, attributes: { memberId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.Member,
        source,
        attributes: {
          memberId: attributes.memberId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92963',
    }),
    memberReactivated: (source: string, attributes: { memberId: string; isBulkAction: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Activated,
        actionSubject: TrackActionSubject.Member,
        source,
        attributes: {
          memberId: attributes.memberId,
          bulkAction: attributes.isBulkAction,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/22145',
    }),
    memberPasswordReset: (attributes: { memberId: string }, source: string): TrackEvent => ({
      data: {
        action: TrackAction.PasswordReset,
        actionSubject: TrackActionSubject.Member,
        source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94024',
    }),
  },
};
